import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Branch from "models/branch";
import Client from "models/client";
import Employee from "models/employee";
import Opportunity, { IOpportunityQueryParam } from "models/opportunity";
import Department from "models/department";
import OpportunityFinance from "models/opportunity-finance";

export interface OpportunityForecastSliceState {
    opportunities: Opportunity[];
    opportunityCount: number;
    departments: Department[];
    branches: Branch[];
    projectManagers: Employee[];
    clients: Client[];
    searchFieldsLoaded: boolean;
    query: IOpportunityQueryParam;
}

export const initialState: OpportunityForecastSliceState = {
    opportunities: [],
    opportunityCount: 0,
    departments: [],
    branches: [],
    projectManagers: [],
    clients: [],
    searchFieldsLoaded: false,
    query: {
        page: 0,
        limit: Number.MAX_SAFE_INTEGER,
        branchIds: [],
        ownerIds: [],
        departmentIds: [],
        clientIds: [],
    },
};

const OpportunityForecastSlice = createSlice({
    name: "opportunity-forecast-slice",
    initialState,
    reducers: {
        setOpportunities(state, { payload }: PayloadAction<Opportunity[]>) {
            state.opportunities = payload;
        },

        setOpportunitiesCount(state, { payload }: PayloadAction<number>) {
            state.opportunityCount = payload;
        },

        setOpportunitiesSearchFields(state, { payload }: PayloadAction<{
            branches: Branch[];
            projectManagers: Employee[];
            departments: Department[];
            clients: Client[];
        }>) {
            state.branches = payload.branches;
            state.projectManagers = payload.projectManagers;
            state.departments = payload.departments;
            state.clients = payload.clients;
            state.searchFieldsLoaded = true;
        },
        mergeOpportunityFinacnes(
            state,
            { payload }: PayloadAction<OpportunityFinance[]>
        ) {

            if (!payload || payload.length === 0) return;

            let opportunities = state.opportunities;

            // 1️⃣ Merge changes into projects
            payload.forEach(opportunityFinance => {
                let opportunity = state.opportunities.find(o => o.id.trim() === opportunityFinance.opportunityId.trim());
                if (!opportunity) return;

                if (opportunity.opportunityFinances) {
                    const index = opportunity.opportunityFinances.findIndex(
                        x => x.fiscalPeriod === opportunityFinance.fiscalPeriod
                    );

                    if (index >= 0) {
                        // update existing period
                        opportunity.opportunityFinances[index].forecastAmount = opportunityFinance.forecastAmount;
                    } else {
                        // insert missing period
                        opportunity.opportunityFinances.push({
                            opportunityId: opportunityFinance.opportunityId,
                            fiscalPeriod: opportunityFinance.fiscalPeriod,
                            forecastAmount: opportunityFinance.forecastAmount,
                        } as any);
                    }
                } else {
                    opportunity.opportunityFinances = [];
                    opportunity.opportunityFinances.push({
                        opportunityId: opportunityFinance.opportunityId,
                        fiscalPeriod: opportunityFinance.fiscalPeriod,
                        forecastAmount: opportunityFinance.forecastAmount,
                    } as any);
                }


            });


            state.opportunities = opportunities;
        },
        setQuery(state, { payload }: PayloadAction<IOpportunityQueryParam>) {
            state.query = payload;
        }
    },
});

export const {
    setOpportunities,
    setOpportunitiesCount,
    setOpportunitiesSearchFields,
    mergeOpportunityFinacnes,
    setQuery
} = OpportunityForecastSlice.actions;

export default OpportunityForecastSlice.reducer;
